import { FloatingActionListMenu } from "client/Components";
import {
	AlertManager,
	useAlerts,
	useSelection,
	useSortHandler,
	useValueEqualMemo
} from "client/Hooks";
import {
	DocumentItemViewModel,
	DocumentItemType,
	DocumentsViewModel,
	DocumentsViewType,
	GridFolderContentsModel,
	ListLocationViewModel,
	ListSortDirection,
	PagePosition,
	Permissions,
	QueryParametersModel,
	SearchScope,
	SharePointColumnId
} from "client/Api/Documents/SharePoint/common";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { reduceToAvailableActions } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { getAuthority } from "client_legacy/ndaw/urlHelper";
import styles from "./SharePointOverView.module.less";
import { SharePointFilter } from "./SharePointSearchForm";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { getFolderContent, getRootContent } from "client/Api/Documents/SharePoint/sharepointDocuments";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import {
	delay,
	ensureNotNil,
	isNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ActionHandlers,
	getFolderActions,
	getNewActions,
	getOpenActions,
	getSelectionActions,
	SharePointDocumentAction,
	SharePointDocumentActionAndOthers,
	SharePointDocumentActionContext
} from "./actions";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "../useEditorPreference";
import { DocumentTemplateState, useDocumentTemplates } from "./useDocumentTemplates";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { SharePointTableView, SharePointTableViewHandle } from "./SharePointTableView";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import {
	SharePointEvent,
	subscribe as sharePointSubscribe
} from "client_legacy/Documents/SharePoint/documentEvents";
import {
	BookmarkEvent,
	subscribe as bookmarkSubscribe
} from "client_legacy/Documents/Bookmarks/events";
import { DocumentStoreType } from "client/Api/Documents/common";
import { SharePointListView, SharePointListViewHandle } from "./SharePointListView";
import { SelectMode } from "client/Misc/commonTypes";
import { useCopyDialog } from "../Dialogs/CopyDialog";
import { useMoveDialog } from "../Dialogs/MoveDialog";
import { useAddBookmarkDialog } from "../Bookmarks/AddBookmarkDialog";
import { SyncClient, TemplateAvailabilityKind } from "client/Api/SharePointSettings/sharepointSettings";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { ColumnVisibilityState } from "./SharePointColumnsVisibility/SharePointColumnsVisibility";
import { SharePointGridView, SharePointGridViewHandle } from "./SharePointGridView";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { SharePointActionsWithContext } from "./SharePointPageContent";

export const gridItemPropertyNameToColumnIdMap: Partial<Record<PropertyPath<GridItem>, SharePointColumnId>> = {
	"name": SharePointColumnId.name,
	"createdOn": SharePointColumnId.dateCreated,
	"modifiedOn": SharePointColumnId.dateModified,
	"createdBy": SharePointColumnId.createdBy,
	"modifiedBy": SharePointColumnId.modifiedBy,
	"extension": SharePointColumnId.extension,
	"size": SharePointColumnId.fileSize,
	"id": SharePointColumnId.id,
	"checkInComment": SharePointColumnId.checkInComment,
	"labels": SharePointColumnId.labels,
	"labelSetting": SharePointColumnId.labelSetting,
	"labelApplied": SharePointColumnId.labelApplied,
	"labelAppliedBy": SharePointColumnId.labelAppliedBy,
	"location": SharePointColumnId.path
};

export const columnIdToNameMapping: { [key in SharePointColumnId]: LocalizationKey; } = {
	[SharePointColumnId.name]: "_Name",
	[SharePointColumnId.dateCreated]: "_DateCreated",
	[SharePointColumnId.dateModified]: "_DateModified",
	[SharePointColumnId.createdBy]: "_CreatedBy",
	[SharePointColumnId.modifiedBy]: "_ModifiedBy",
	[SharePointColumnId.extension]: "_Extension",
	[SharePointColumnId.fileSize]: "_FileSize",
	[SharePointColumnId.id]: "_Id",
	[SharePointColumnId.checkInComment]: "_CheckInComment",
	[SharePointColumnId.labels]: "_Labels",
	[SharePointColumnId.labelSetting]: "_LabelSetting",
	[SharePointColumnId.labelApplied]: "_LabelApplied",
	[SharePointColumnId.labelAppliedBy]: "_LabelAppliedBy",
	[SharePointColumnId.path]: "_Location"
};

export type SharePointOverViewProps = {
	view: DocumentsViewModel;
	folder: Folderish;
	onOpenFolder(folder: Folderish): void;
	onFolderPermissionsLoaded(
		viewId: string,
		folderId: number | null,
		permissions: Permissions
	): void;
	filter: SharePointFilter;
	sortingState: DataSourceSortableState<GridItem>;
	setSortingState: Dispatch<SetStateAction<DataSourceSortableState<GridItem>>>;
	isMobile: boolean;
	isResponsive: boolean;
	isSharingViaLinkEnabled: boolean;
	maxNumberOfFilesToDownloadAsZip: number;
	syncClient: SyncClient;
	templateAvailabilityKind: TemplateAvailabilityKind;
	columnVisibilities: readonly ColumnVisibilityState[];
	layoutKind: DocumentLayoutKind;
	onActionsChanged: (value: SharePointActionsWithContext) => (() => void);
	onUploadFolder: () => void;
	onUploadFiles: () => void;
};

export const SharePointOverView = (props: SharePointOverViewProps): ReactElement => {
	const {
		view,
		folder,
		onOpenFolder,
		onFolderPermissionsLoaded,
		filter,
		sortingState,
		setSortingState,
		isMobile,
		isResponsive,
		isSharingViaLinkEnabled,
		maxNumberOfFilesToDownloadAsZip,
		syncClient,
		templateAvailabilityKind,
		columnVisibilities,
		layoutKind,
		onActionsChanged,
		onUploadFiles,
		onUploadFolder
	} = props;

	const viewKind: DocumentsViewType = view.type;
	if (viewKind === DocumentsViewType.RecycleBinView) {
		throw new Error("Cannot be a recycle bin view");
	}

	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [selectMode, setSelectMode] = useState<SelectMode>(SelectMode.Disabled);

	const [pagePositions, setPagePositions] = useState<Map<number, PagePosition | null>>(
		() => createDefaultPagePositionMap()
	);

	const [items, setItems] = useState<readonly GridItem[]>([]);

	const [selectedItems, setSelectedItems] = useState<readonly GridItem[]>([]);

	const tableRef: RefObject<SharePointTableViewHandle | null> = useRef<SharePointTableViewHandle>(null);
	const listRef: RefObject<SharePointListViewHandle | null> = useRef<SharePointListViewHandle>(null);
	const gridRef: RefObject<SharePointGridViewHandle | null> = useRef<SharePointGridViewHandle>(null);

	const alertManager: AlertManager = useAlerts();

	const editorPreference: UserEditorPreference = useEditorPreference();

	const { sortingFor } = useSortHandler<GridItem>(
		sortingState,
		setSortingState,
		true
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		items,
		"uniqueKey",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const documentTemplateState: DocumentTemplateState = useDocumentTemplates(
		templateAvailabilityKind,
		view
	);

	const {
		dialogElements: actionsDialogs,
		actionContext,
		folderActions,
		selectionActions,
		newActions,
		openItemHandler,
	 } = useSharePointActionsAndHandlers(
		view,
		folder,
		selectedItems,
		setSelectedItems,
		selectMode,
		setSelectMode,
		items,
		filter,
		isSharingViaLinkEnabled,
		maxNumberOfFilesToDownloadAsZip,
		syncClient,
		isMobile,
		isResponsive,
		documentTemplateState,
		editorPreference,
		alertManager,
		onOpenFolder,
		onUploadFolder,
		onUploadFiles
	);

	const allActions = useMemo(
		(): readonly SharePointDocumentActionAndOthers[] => [...folderActions, ...selectionActions],
		[folderActions, selectionActions]
	);

	useEffect(
		(): (() => void) => {
			return onActionsChanged({
				context: actionContext,
				actions: allActions
			});
		},
		[
			allActions,
			actionContext,
			onActionsChanged
		]
	);

	useEffect(
		() => {
			if (selectMode === SelectMode.FromLongTouch && selectedItems.length === 0) {
				setSelectMode(SelectMode.Disabled);
			}
		},
		[
			selectMode,
			selectedItems.length,
			setSelectMode
		]
	);

	useEffect(
		(): void => {
			if (selectMode === SelectMode.Disabled
				&& isMobile
			) {
				setSelectedItems([]);
			}
		},
		[selectMode, isMobile]
	);

	const queryParameters: QueryParametersModel = useQueryParameters(
		currentPageNumber,
		pagePositions,
		sortingState,
		filter
	);

	const locationParameters: SharePointLocationParameters = useSharePointLocationParameters(view, folder);

	useEffect(
		() => {
			hasImplicitDependencyOn(
				locationParameters.view.id,
				locationParameters.folder.id,
				queryParameters.sortingField,
				queryParameters.sortingDirection,
				queryParameters.filterText,
				queryParameters.searchScope
			);

			setCurrentPageNumber(0);
			setPagePositions(createDefaultPagePositionMap());
			setItems([]);

			tableRef.current
				?.resetScrollPosition();

			listRef.current
				?.resetScrollPosition();

			gridRef.current
				?.resetScrollPosition();
		},
		[
			locationParameters.view.id,
			locationParameters.folder.id,
			queryParameters.sortingField,
			queryParameters.sortingDirection,
			queryParameters.filterText,
			queryParameters.searchScope
		]
	);

	useEffect(
		(): (() => void) => {

			const loadPage = async (
				queryParameters: QueryParametersModel,
				signal: AbortSignal
			): Promise<void> => {

				setIsBusy(true);

				const {
					view,
					folder
				} = locationParameters;

				try {
					// Wait for all the dependencies which trigger a load to settle to prevent
					// requests getting aborted
					await delay(10, signal);

					const folderContent: GridFolderContentsModel = await retrieveSharepointFolderContent(
						folder,
						queryParameters,
						view.type,
						signal
					);

					const {
						items,
						pagerParameters,
						permissions
					} = folderContent;

					setPagePositions(
						x => {
							const result = new Map<number, PagePosition | null>(x);
							result.set(currentPageNumber, pagerParameters.nextPagePosition);
							return result;
						}
					);

					const newGridItems: GridItem[] = items
						.map(
							(x) => toGridItem(x, folder)
						);

					setItems(
						(x) => {
							const newUniqueKeys = new Set<string>(
								x.map(x => x.uniqueKey)
							);

							const result: GridItem[] = [...x];
							for (const item of newGridItems) {
								if (newUniqueKeys.has(item.uniqueKey)) {
									continue;
								}

								result.push(item);
							}

							return result;
						}
					);

					if (permissions) {
						onFolderPermissionsLoaded(
							view.id,
							folder.id,
							permissions
						);
					}

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_UnableToRetrieveItems"));
				}
			};

			const cancellation = new AbortController();
			loadPage(queryParameters, cancellation.signal);
			return () => cancellation.abort();
		},
		[
			locationParameters,
			queryParameters,
			currentPageNumber,
			onFolderPermissionsLoaded,
			alertManager
		]
	);

	useSharePointPubSubEvents(
		locationParameters.view.id,
		locationParameters.folder,
		setItems
	);

	const loadMoreItemsHandler = useCallback(
		(): void => {
			setCurrentPageNumber(x => x + 1);
		},
		[]
	);

	const renderContent = (): ReactElement => {
		const hasMoreItems: boolean = pagePositions.has(currentPageNumber)
			&& pagePositions.get(currentPageNumber) !== null;

		if (!isMobile && layoutKind === DocumentLayoutKind.List) {
			return (
				<SharePointTableView
					isBusy={isBusy}
					hasMoreItems={hasMoreItems}
					onLoadMoreItems={loadMoreItemsHandler}
					loadMoreIndicatorVersion={currentPageNumber}
					sortingFor={sortingFor}
					multiSelection={multiSelection}
					selectionFor={selectionFor}
					columnVisibilities={columnVisibilities}
					items={items}
					onOpenItem={openItemHandler}
					selectionActions={selectionActions}
					actionContext={actionContext}
					ref={tableRef}
					hasSearch={isNotNilOrEmpty(filter.text.value)}
				/>
			);
		}

		if (layoutKind === DocumentLayoutKind.Grid) {
			return (
				<SharePointGridView
					ref={gridRef}
					isBusy={isBusy}
					hasMoreItems={hasMoreItems}
					loadMoreIndicatorVersion={currentPageNumber}
					multiSelection={multiSelection}
					selectionFor={selectionFor}
					items={items}
					selectionActions={selectionActions}
					actionContext={actionContext}
					isInSelectMode={selectMode !== SelectMode.Disabled}
					onOpenItem={openItemHandler}
					onLoadMoreItems={loadMoreItemsHandler}
					onEnableSelectMode={() => setSelectMode(SelectMode.FromLongTouch)}
					hasSearch={isNotNilOrEmpty(filter.text.value)}
				/>
			);
		}

		return (
			<SharePointListView
				isBusy={isBusy}
				hasMoreItems={hasMoreItems}
				onLoadMoreItems={loadMoreItemsHandler}
				loadMoreIndicatorVersion={currentPageNumber}
				items={items}
				selectionFor={selectionFor}
				isInSelectMode={selectMode !== SelectMode.Disabled}
				onEnableSelectMode={() => setSelectMode(SelectMode.FromLongTouch)}
				onOpenItem={openItemHandler}
				selectionActions={selectionActions}
				actionContext={actionContext}
				ref={listRef}
				hasSearch={isNotNilOrEmpty(filter.text.value)}
			/>
		);

	};

	return (
		<div className={styles.root}>
			<div className={styles.itemsContainer}>
				{renderContent()}
			</div>

			<FloatingActionListMenu
				actions={newActions}
				actionContext={actionContext}
				menuTitle={localize("_Create")}
				trackingId="sp-documents-fab-menu"
			/>
			{actionsDialogs}
		</div>
	);
};
SharePointOverView.displayName = "SharePointOverView";

export function createDefaultPagePositionMap(): Map<number, PagePosition | null> {
	const result = new Map<number, PagePosition | null>();
	result.set(0, null);
	return result;
}

export function retrieveSharepointFolderContent(
	openedFolder: SlimFolderish,
	queryParameters: QueryParametersModel,
	currentViewType: DocumentsViewType,
	signal: AbortSignal
): Promise<GridFolderContentsModel> {
	if (openedFolder.isRoot === true) {
		return getRootContent(
			{
				location: convertToLocationModel(openedFolder.location),
				queryParameters: queryParameters
			},
			signal
		);
	} else {
		return getFolderContent(
			{
				id: openedFolder.id,
				folderLocation: convertToLocationModel(openedFolder.location),
				serverRelativeUrl: openedFolder.serverRelativeUrl,
				queryParameters: queryParameters,
				// in search results, there are no permissions filled in for every item,
				// so retrieve permissions from the server to set up the action bar correctly after the folder contents is shown
				retrieveFolderPermissions: currentViewType === DocumentsViewType.SharedWithMeView
					? true
					: undefined
			},
			signal
		);
	}
}

export type GridItem = {
	id: number;
	uniqueKey: string;
	name: string;
	createdOn: Date;
	modifiedOn: Date;
	createdBy: string;
	modifiedBy: string | nil;
	extension: string | nil;
	size: number | nil;
	checkInComment: string | nil;
	labels: string | nil;
	labelSetting: string | nil;
	labelApplied: Date | nil;
	labelAppliedBy: string | nil;
	isInfected: boolean | nil;
	location: string | nil;
	model: DocumentItemViewModel;
};

export function generateSharePointUniqueKey(document: DocumentItemViewModel): string;
export function generateSharePointUniqueKey(documentId: number, location: ListLocationViewModel): string;
export function generateSharePointUniqueKey(
	documentOrDocumentId: DocumentItemViewModel | number,
	location?: ListLocationViewModel
): string {
	let documentId: number;
	if (typeof documentOrDocumentId === "number") {
		documentId = documentOrDocumentId;
		if (!location) {
			throw new Error("Location is not provided for documentId");
		}
	} else {
		documentId = documentOrDocumentId.id;
		location = documentOrDocumentId.location;
	}

	return [
		location.siteUrl,
		location.listId,
		documentId
	].join("_");
}

export function toGridItem(
	document: DocumentItemViewModel,
	currentFolder: SlimFolderish
): GridItem {
	let size: number | nil;
	let checkInComment: string | nil;
	let labels: string | nil;
	let labelSetting: string | nil;
	let labelApplied: Date | nil;
	let labelAppliedBy: string | nil;
	let isInfected: boolean | nil;

	let location: string | nil = getLocation(document, currentFolder);

	if (location === "/") {
		location = null;
	}

	if (document.itemType === DocumentItemType.File) {
		size = document.size;
		checkInComment = document.checkInComment;
		labels = document.labels;
		labelSetting = document.labelSetting
			?.toString();
		labelAppliedBy = document.labelAppliedBy
			?.toString();
		labelApplied = parseDtoDate(document.labelAppliedDateTime);
		isInfected = document.isInfected;
	}

	return {
		id: document.id,
		uniqueKey: generateSharePointUniqueKey(document),
		name: document.name,
		createdOn: parseDtoDate(document.createdOn),
		modifiedOn: parseDtoDate(document.modifiedOn),
		createdBy: document.createdBy,
		modifiedBy: document.modifiedBy,
		extension: document.extension,
		size,
		checkInComment,
		labels,
		labelSetting,
		labelApplied,
		labelAppliedBy,
		isInfected,
		location,
		model: document
	};
}

function getLocation(
	document: DocumentItemViewModel,
	currentFolder: SlimFolderish
): string {
	if (currentFolder.isRoot === true) {
		const siteRelativeUrl: string = currentFolder.location
			.siteUrl
			.substring(getAuthority(currentFolder.location.siteUrl).length);

		const indexOfCurrentSitePath: number = document.serverRelativeUrl.toUpperCase()
			.indexOf(siteRelativeUrl.toUpperCase());

		if (indexOfCurrentSitePath === 0) {
			const intermediateResult: string = document.serverRelativeUrl.substring(siteRelativeUrl.length);
			const segments: string[] = intermediateResult.split("/");
			segments.splice(0, 2); // Root folder name segment
			segments.splice(-1, 1); // Document name segment
			return segments.length === 0
				? "/"
				: `/${segments.join("/")}/`;
		}
	} else {
		const indexOfCurrentFolderPath: number = document.serverRelativeUrl.toUpperCase()
			.indexOf(currentFolder.serverRelativeUrl.toUpperCase());

		if (indexOfCurrentFolderPath === 0) {
			return document.serverRelativeUrl
				.substring(
					currentFolder.serverRelativeUrl.length,
					// It appears SP gets drunk sometimes and returns the current folder as a result, and an end argument
					// which is less then the start argument of the substring makes it return the range between end and start
					Math.max(currentFolder.serverRelativeUrl.length, document.serverRelativeUrl.length - document.name.length)
				);
		}
	}

	return getAuthority(document.location.siteUrl) + document.serverRelativeUrl;
}

function useQueryParameters(
	currentPageNumber: number,
	pagePositions: Map<number, PagePosition | null>,
	sortingState: DataSourceSortableState<GridItem>,
	filter: SharePointFilter
): QueryParametersModel {

	const currentPagePosition = useMemo(
		(): PagePosition | null => {
			return currentPageNumber === 0
				? null
				: ensureNotNil(
					pagePositions.get(currentPageNumber - 1)
				);
		},
		[currentPageNumber, pagePositions]
	);

	return useValueEqualMemo(
		(): QueryParametersModel => {
			if (sortingState.direction === DataSourceSortDirection.None) {
				throw new Error("An incorrect sorting is specified");
			}

			const {
				scope,
				text: {
					value: searchTerm
				}
			} = filter;

			return {
				itemsCount: documentsConfiguration().sharePoint
					.pageSize,
				sortingField: ensureNotNil(gridItemPropertyNameToColumnIdMap[sortingState.propertyName]),
				sortingDirection: sortingState.direction === DataSourceSortDirection.Ascending
					? ListSortDirection.Ascending
					: ListSortDirection.Descending,
				pagePosition: currentPagePosition,
				fileTypeFilter: null,
				filterText: searchTerm,
				searchScope: scope
			};
		},
		[
			filter,
			currentPagePosition,
			sortingState
		]
	);
}

export type SlimSharePointView = {
	id: string;
	type: DocumentsViewType;
};

export type SlimFolderish =
	{
		isRoot: true;
		id: null;
		location: ListLocationViewModel;
		viewId: string;
	}
	|
	{
		isRoot: false;
		id: number;
		location: ListLocationViewModel;
		viewId: string;
		serverRelativeUrl: string;
	};

export type SharePointLocationParameters = {
	view: SlimSharePointView;
	folder: SlimFolderish;
};

function toSlimView(view: DocumentsViewModel): SlimSharePointView {
	const { id, type } = view;

	return {
		id,
		type
	};
};

function toSlimFolderish(folder: Folderish): SlimFolderish {
	if (folder.isRoot) {
		return {
			isRoot: true,
			id: folder.id,
			viewId: folder.viewId,
			location: folder.location
		};
	}

	return {
		isRoot: false,
		id: folder.id,
		viewId: folder.viewId,
		location: folder.location,
		serverRelativeUrl: folder.serverRelativeUrl
	};
}

export function useSharePointLocationParameters(
	view: DocumentsViewModel,
	folder: Folderish
): SharePointLocationParameters {

	const parameters = useMemo(
		(): SharePointLocationParameters => {
			return {
				view: toSlimView(view),
				folder: toSlimFolderish(folder)
			};
		},
		[view, folder]
	);

	return useValueEqualMemo(
		() => parameters,
		[parameters]
	);
}

type SharePointActionsAndHandlers = {
	actionContext: SharePointDocumentActionContext;
	folderActions: readonly SharePointDocumentActionAndOthers[];
	selectionActions: readonly SharePointDocumentAction[];
	newActions: readonly SharePointDocumentActionAndOthers[];
	dialogElements: ReactElement;
	openItemHandler: (item: DocumentItemViewModel) => void;
};

export function useSharePointActionsAndHandlers(
	view: DocumentsViewModel,
	folder: Folderish,
	selectedItems: readonly GridItem[],
	setSelectedItems: Dispatch<SetStateAction<readonly GridItem[]>>,
	selectMode: SelectMode,
	setSelectMode : Dispatch<SetStateAction<SelectMode>>,
	itemsOnPage: readonly GridItem[],
	filter: SharePointFilter,
	isSharingViaLinkEnabled: boolean,
	maxNumberOfFilesToDownloadAsZip: number,
	syncClient: SyncClient,
	isMobile: boolean,
	isResponsive: boolean,
	documentTemplateState: DocumentTemplateState,
	editorPreference: UserEditorPreference,
	alertManager: AlertManager,
	onOpenFolder: (folder: Folderish)=> void,
	onUploadFolder: () => void,
	onUploadFiles: () => void
): SharePointActionsAndHandlers {
	const [copyDialog, openCopyDialog] = useCopyDialog();
	const [moveDialog, openMoveDialog] = useMoveDialog();
	const [addBookmarkDialog, openAddBookmarkDialog] = useAddBookmarkDialog();

	const isInSelectMode: boolean = selectMode !== SelectMode.Disabled;

	const copyItemHandler = useCallback(
		(selection: readonly DocumentItemViewModel[]) => {
			openCopyDialog({
				thingsToCopy: {
					storeKind: DocumentStoreType.SharePoint,
					documents: selection,
					source: {
						kind: DocumentStoreType.SharePoint,
						folder: {...folder, view}
					}
				}
			});
		},
		[
			folder,
			openCopyDialog,
			view
		]
	);

	const moveItemHandler = useCallback(
		(selection: readonly DocumentItemViewModel[]) => {
			openMoveDialog({
				thingsToMove: {
					storeKind: DocumentStoreType.SharePoint,
					source: {
						kind: DocumentStoreType.SharePoint,
						folder: {...folder, view}
					},
					documents: selection
				}
			});
		},
		[
			folder,
			openMoveDialog,
			view
		]
	);

	const handlers = useMemo(
		(): ActionHandlers => {
			return {
				openFolder: (folder) => {
					onOpenFolder({ ...folder, isRoot: false });
				},
				selectItem: (item) => {
					if (isMobile && selectMode === SelectMode.Disabled) {
						return;
					}

					const itemUniqueKey: string = generateSharePointUniqueKey(item);
					setSelectedItems(
						[
							ensureNotNil(
								itemsOnPage.find(x => x.uniqueKey === itemUniqueKey)
							)
						]
					);
				},
				enableSelectMode: () => setSelectMode(SelectMode.Manual),
				disableSelectMode: () => setSelectMode(SelectMode.Disabled),
				selectAll: () => setSelectedItems(itemsOnPage),
				deselectAll: () => setSelectedItems([]),
				onCopyItems: copyItemHandler,
				onMoveItems: moveItemHandler,
				addBookmark: (item: DocumentItemViewModel): void => {
					openAddBookmarkDialog({
						store: DocumentStoreType.SharePoint,
						item
					});
				},
				uploadFolder: onUploadFolder,
				uploadFiles: onUploadFiles
			};
		},
		[
			copyItemHandler,
			moveItemHandler,
			onOpenFolder,
			setSelectMode,
			setSelectedItems,
			isMobile,
			selectMode,
			itemsOnPage,
			onUploadFiles,
			onUploadFolder,
			openAddBookmarkDialog
		]
	);

	const openItemHandler = (item: DocumentItemViewModel): void => {
		const context: SharePointDocumentActionContext = {
			...actionContext,
			selection: [item]
		};

		const actions: readonly SharePointDocumentAction[] = getOpenActions(
			handlers,
			itemsOnPage.map(x => x.model),
			alertManager,
			editorPreference
		);

		reduceToAvailableActions(actions, context)[0]
			?.execute(context);
	};

	const isInSearchResults: boolean = filter.scope === SearchScope.Subfolders
		&& (filter.text.value !== "");

	const { isVersioningEnabled } = view;

	const actionContext = useMemo(
		(): SharePointDocumentActionContext => {
			return {
				view,
				folder,
				selection: selectedItems.map(x => x.model),
				isInSearchResults,
				isSharingViaLinkEnabled,
				isVersioningEnabled,
				maxNumberOfFilesToDownloadAsZip,
				syncClient,
				isMobile,
				isInSelectMode
			};
		},
		[
			view,
			folder,
			selectedItems,
			isInSearchResults,
			isSharingViaLinkEnabled,
			isVersioningEnabled,
			maxNumberOfFilesToDownloadAsZip,
			syncClient,
			isMobile,
			isInSelectMode
		]
	);

	const selectionActions = useMemo(
		(): readonly SharePointDocumentAction[] => {
			return getSelectionActions(
				handlers,
				itemsOnPage.map(x => x.model),
				alertManager,
				editorPreference
			);
		},
		[
			handlers,
			alertManager,
			itemsOnPage,
			editorPreference
		]
	);

	const folderActions = useMemo(
		(): readonly SharePointDocumentActionAndOthers[] => {
			return getFolderActions(
				documentTemplateState,
				itemsOnPage.map(x => x.model),
				handlers,
				!isResponsive
			);
		},
		[
			documentTemplateState,
			itemsOnPage,
			handlers,
			isResponsive
		]
	);

	const newActions = useMemo(
		(): readonly SharePointDocumentActionAndOthers[] => {
			return getNewActions(
				documentTemplateState,
				false
			);
		},
		[documentTemplateState]
	);

	const dialogElements: ReactElement = (
		<>
			{copyDialog}
			{moveDialog}
			{addBookmarkDialog}
		</>
	);

	return {
		actionContext,
		folderActions,
		selectionActions,
		newActions,
		dialogElements,
		openItemHandler
	};
}

export function useSharePointPubSubEvents(
	viewId: string,
	folder: SlimFolderish,
	setItems: Dispatch<SetStateAction<readonly GridItem[]>>
): void {
	useEffect(
		(): (() => void) => {
			const subscriptionTokens: SubscriptionTokenCollection = registerSubscriptions(
				viewId,
				folder,
				setItems
			);

			return () => subscriptionTokens.destroy();
		},
		[
			viewId,
			folder,
			setItems
		]
	);
}

function registerSubscriptions(
	viewId: string,
	folder: SlimFolderish,
	setItems: Dispatch<SetStateAction<readonly GridItem[]>>
): SubscriptionTokenCollection {
	const result = new SubscriptionTokenCollection();

	const itemAddedToken: PubSubSubscriptionToken = sharePointSubscribe(
		SharePointEvent.AddedOrReplaced,
		(_, item: DocumentItemViewModel) => {
			if (item.viewId !== viewId || item.parentFolderId !== folder.id) {
				return;
			}

			setItems(
				x => {
					const copy: GridItem[] = [...x];

					const gridItem: GridItem = toGridItem(item, folder);
					const index: number = copy.findIndex(x => x.uniqueKey === gridItem.uniqueKey);
					if (index === -1) {
						copy.unshift(gridItem);
					} else {
						copy[index] = gridItem;
					}

					return copy;
				}
			);
		}
	);

	result.add(itemAddedToken);

	const itemRenamedToken: PubSubSubscriptionToken = sharePointSubscribe(
		SharePointEvent.Renamed,
		(_, data) => {
			const { newDocument } = data;

			const uniqueKey: string = generateSharePointUniqueKey(newDocument.id, newDocument.location);
			const newItem: GridItem = toGridItem(newDocument, folder);

			setItems(
				items => {
					return items.map(
						x => {
							if (x.uniqueKey === uniqueKey) {
								return {
									...newItem,
									model: {
										...newItem.model,
										bookmarkId: x
											.model
											.bookmarkId
									}
								};
							}

							return x;
						}
					);
				}
			);
		}
	);

	result.add(itemRenamedToken);

	const itemDeletedToken: PubSubSubscriptionToken = sharePointSubscribe(
		SharePointEvent.Deleted,
		(_, documents) => {
			setItems(
				x => {
					const uniqueKeys: string[] = documents.map(
						x => generateSharePointUniqueKey(x.id, x.location)
					);

					const copy: GridItem[] = x.filter(
						x => !uniqueKeys.includes(x.uniqueKey)
					);

					return copy.length === x.length
						? x
						: copy;
				}
			);
		}
	);

	result.add(itemDeletedToken);

	const bookmarkAddedToken: PubSubSubscriptionToken = bookmarkSubscribe(
		BookmarkEvent.Added,
		(_, model) => {
			if (model.storeType !== DocumentStoreType.SharePoint) {
				return;
			}

			const documentUniqueKey: string = generateSharePointUniqueKey(model.itemId, model.location);

			setItems(
				x => {
					const index: number = x.findIndex(x => x.uniqueKey === documentUniqueKey);
					if (index === -1) {
						return x;
					}

					const copy: GridItem[] = [...x];
					const document: DocumentItemViewModel = {
						...copy[index].model,
						bookmarkId: model.id
					};

					copy[index] = toGridItem(document, folder);

					return copy;
				}
			);
		}
	);

	result.add(bookmarkAddedToken);

	const bookmarkDeletedToken: PubSubSubscriptionToken = bookmarkSubscribe(
		BookmarkEvent.Deleted,
		(_, bookmarkIds) => {
			setItems(
				x => {
					return x.map(gridItem => {
						if (isNil(gridItem.model.bookmarkId)
							|| !bookmarkIds.includes(gridItem.model.bookmarkId)
						) {
							return gridItem;
						}

						const document: DocumentItemViewModel = {
							...gridItem.model,
							bookmarkId: null
						};

						return toGridItem(document, folder);
					});
				}
			);
		}
	);

	result.add(bookmarkDeletedToken);

	return result;
}
